import { useEffect, useMemo, useState } from "react";
import { AlertOctagon } from "lucide-react";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import EmptyState from "@/components/EmptyState";
import {
    INCIDENT_STATE_LABELS,
    INCIDENT_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";

export default function PortalIncidents() {
    const [items, setItems] = useState([]);
    const [props, setProps] = useState([]);
    const [filter, setFilter] = useState("active");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.get("/portal/incidents"), api.get("/portal/properties")])
            .then(([i, p]) => {
                setItems(i.data || []);
                setProps(p.data || []);
            })
            .finally(() => setLoading(false));
    }, []);

    const propsById = useMemo(() => Object.fromEntries(props.map((p) => [p.id, p])), [props]);
    const ACTIVE_STATES = ["open", "assigned", "in_progress", "awaiting_material"];

    const filtered = useMemo(() => {
        if (filter === "active") return items.filter((i) => ACTIVE_STATES.includes(i.state));
        if (filter === "resolved") return items.filter((i) => ["resolved", "closed"].includes(i.state));
        return items;
    }, [items, filter]);

    return (
        <div className="space-y-6" data-testid="portal-incidents-page">
            <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                    Soporte y mantenimiento
                </p>
                <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                    Incidencias
                </h2>
                <p className="text-sm text-zentra-muted mt-1">
                    {items.length} en total · {filtered.length} en este filtro
                </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <FilterChip active={filter === "active"} onClick={() => setFilter("active")} label="Activas" testId="portal-inc-filter-active" />
                <FilterChip active={filter === "resolved"} onClick={() => setFilter("resolved")} label="Resueltas" testId="portal-inc-filter-resolved" />
                <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Todas" testId="portal-inc-filter-all" />
            </div>

            {loading ? (
                <div className="zentra-skeleton h-72" />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={AlertOctagon}
                    title={filter === "active" ? "Sin incidencias activas" : "Sin coincidencias"}
                    description="Tus propiedades están al día."
                />
            ) : (
                <div className="zentra-card-static overflow-hidden">
                    <table className="zentra-table" data-testid="portal-incidents-table">
                        <thead>
                            <tr>
                                <th>Título</th>
                                <th>Propiedad</th>
                                <th>Tipo</th>
                                <th>Prioridad</th>
                                <th>Estado</th>
                                <th>Creada</th>
                                <th>Resuelta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((i) => {
                                const p = propsById[i.property_id];
                                return (
                                    <tr key={i.id} data-testid={`portal-inc-row-${i.id}`}>
                                        <td>
                                            <p className="font-medium text-zentra-ink">{i.title}</p>
                                            {i.description && (
                                                <p className="text-xs text-zentra-muted line-clamp-1">{i.description}</p>
                                            )}
                                            {i.resolution_note && (
                                                <p className="text-xs text-emerald-700 mt-0.5">
                                                    ✓ {i.resolution_note}
                                                </p>
                                            )}
                                        </td>
                                        <td>
                                            <p className="text-sm">{p?.name || "—"}</p>
                                            <p className="text-[10px] font-mono-zentra text-zentra-muted">{p?.code}</p>
                                        </td>
                                        <td className="text-sm">{INCIDENT_TYPE_LABELS[i.type] || i.type}</td>
                                        <td>
                                            <StateBadge value={i.priority} dict={PRIORITY_LABELS} />
                                        </td>
                                        <td>
                                            <StateBadge value={i.state} dict={INCIDENT_STATE_LABELS} />
                                        </td>
                                        <td className="text-xs font-mono-zentra text-zentra-muted">
                                            {(i.created_at || "").slice(0, 10) || "—"}
                                        </td>
                                        <td className="text-xs font-mono-zentra text-zentra-muted">
                                            {(i.resolved_at || "").slice(0, 10) || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function FilterChip({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition border ${
                active
                    ? "bg-zentra-primary text-white border-zentra-primary"
                    : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-olive"
            }`}
        >
            {label}
        </button>
    );
}
